import React, { useCallback, useState } from 'react'
import { createRoot, Root } from 'react-dom/client'

import CommonModal, { ICommonModalProps } from './CommonModal'

type ReactNodeOrFunction =
  | React.ReactNode
  | ((close: () => void) => React.ReactNode)

type PromisifiedCommonModalProps = Omit<
  ICommonModalProps,
  'open' | 'onOpenChange' | 'body' | 'footer'
> & {
  body: ReactNodeOrFunction
  footer?: ReactNodeOrFunction
}

interface PromisifiedCommonModalResult {
  open: (props: PromisifiedCommonModalProps) => Promise<void>
}

const createPromisifiedCommonModal = (): PromisifiedCommonModalResult => {
  const ModalWrapper: React.FC<
    PromisifiedCommonModalProps & { resolve: () => void }
  > = props => {
    const [isOpen, setIsOpen] = useState(true)
    const { resolve, body, footer, ...modalProps } = props

    const handleClose = useCallback(() => {
      setIsOpen(false)
      resolve()
    }, [resolve])

    const renderContent = (content: ReactNodeOrFunction): React.ReactNode => {
      if (typeof content === 'function') {
        return content(handleClose)
      }
      return content
    }

    return (
      <CommonModal
        {...modalProps}
        open={isOpen}
        onOpenChange={open => !open && handleClose()}
        body={renderContent(body)}
        footer={footer ? renderContent(footer) : undefined}
      />
    )
  }

  const open = (props: PromisifiedCommonModalProps): Promise<void> => {
    return new Promise<void>(resolve => {
      const modalRoot = document.createElement('div')
      document.body.appendChild(modalRoot)

      const root: Root = createRoot(modalRoot)

      const cleanup = () => {
        root.unmount()
        modalRoot.remove()
      }

      root.render(
        <ModalWrapper
          {...props}
          resolve={() => {
            resolve()
            cleanup()
          }}
        />
      )
    })
  }

  return { open }
}

export const asyncModal = createPromisifiedCommonModal()

export default CommonModal
